import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { StatusBadge, ModeBadge } from '../components/AssessmentBadges';
import { HazardTable, FullQRATable } from '../components/AssessmentTables';
import { FragranceResultsSection } from '../components/FragranceModule';
import { ClaimResultsSection } from '../components/ClaimsModule';
import { SiteHeader } from '../components/SiteLayout';
import StructureLookupWidget from '../components/StructureLookupWidget';
import CopilotNotesPanel from '../components/CopilotNotesPanel';
import VersionDiffSection from '../components/VersionDiffSection';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Flask, ArrowLeft, DownloadSimple, Info, Microscope, ArrowRight, Warning, BookOpen, PawPrint, GitBranch, Plus, X
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Pet Assessment Section — rendered when the formulation was created as a Pet Product.
 * Displays the auto-triggered species-specific safety screen. */
function PetAssessmentSection({ formulation }) {
  const petData = formulation?.pet_assessments;
  if (!petData || Object.keys(petData).length === 0) return null;

  const statusCls = {
    acceptable:     'bg-[#DEF7EC] border-[#31C48D] text-[#03543F]',
    needs_review:   'bg-[#FEF3C7] border-[#F3CC64] text-[#723B13]',
    not_acceptable: 'bg-[#FDE8E8] border-[#F8B4B4] text-[#9B1C1C]',
  };

  return (
    <div className="card-flat mb-8" data-testid="pet-assessment-section">
      <div className="p-4 border-b border-gray-200 flex items-center gap-2">
        <PawPrint size={20} className="text-[#111827]" weight="duotone" />
        <h2 className="text-lg font-semibold text-[#111827]">Pet Product Safety Screen</h2>
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded">auto-triggered</span>
      </div>

      {Object.entries(petData).map(([key, pet]) => {
        const riskyIngs = (pet.results || []).filter(x => x.species_toxicity || x.risk_level === 'not_acceptable');
        const overallCls = statusCls[pet.overall_status] || statusCls.needs_review;
        return (
          <div key={key} className="p-4 border-b border-gray-100 last:border-b-0" data-testid={`pet-block-${pet.species}`}>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-sm font-semibold text-[#111827]">
                {pet.species_label} · {pet.size} · {pet.body_weight} kg
              </span>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${overallCls}`}>
                {pet.overall_status.replace('_', ' ')}
              </span>
              <span className="text-[10px] text-[#4B5563] ml-auto">
                grooming factor <span className="font-mono tabular-nums">{pet.grooming_oral_factor}</span>
              </span>
            </div>
            {pet.species_notes && (
              <p className="text-xs text-[#4B5563] mb-2">{pet.species_notes}</p>
            )}
            {pet.species === 'cat' && (
              <p className="text-[11px] text-[#9B1C1C] flex items-center gap-1 mb-2">
                <Warning size={12} weight="fill" /> Cats groom extensively — oral exposure is elevated and glucuronidation is deficient.
              </p>
            )}
            {riskyIngs.length > 0 ? (
              <div className="space-y-1 mt-2">
                {riskyIngs.map(x => (
                  <div key={x.ingredient_name} className="flex items-start gap-1.5 text-xs p-2 bg-[#FDE8E8] border border-[#F8B4B4] rounded text-[#9B1C1C]">
                    <Warning size={12} weight="fill" className="mt-0.5 flex-shrink-0" />
                    <span><b>{x.ingredient_name}:</b> {x.notes || x.interpretation}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#03543F]">No species-specific concerns identified at current concentrations.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SupportingDocuments({ documents, onDownload }) {
  if (documents.length === 0) return null;
  return (
    <div className="card-flat overflow-hidden mb-8">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-[#111827]">Supporting Documents</h2>
      </div>
      <div className="divide-y divide-gray-200">
        {documents.map((doc) => (
          <div key={doc.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
            <div>
              <p className="text-sm font-medium text-[#111827]">{doc.original_filename}</p>
              <p className="text-xs text-[#4B5563]">{(doc.size / 1024).toFixed(1)} KB · {doc.document_type}</p>
            </div>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => onDownload(doc)} data-testid={`download-doc-${doc.id}`}>
              <DownloadSimple size={16} className="mr-1" /> Download
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExposureAssumptions({ metadata, mode }) {
  if (mode !== 'full' || !metadata.product_category) return null;
  return (
    <div className="mt-4 p-4 bg-[#F9FAFB] border border-gray-200 rounded-sm" data-testid="exposure-assumptions-panel">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2">
        Exposure Assumptions {metadata.use_custom_exposure && '(Custom Override)'}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        {metadata.frequency_of_use && (
          <div><span className="text-[#4B5563]">Frequency:</span>{' '}<span className="font-mono font-semibold">{metadata.frequency_of_use}x/day</span></div>
        )}
        {metadata.product_category && (
          <div><span className="text-[#4B5563]">Category:</span>{' '}<span className="font-semibold">{metadata.product_category}</span></div>
        )}
      </div>
    </div>
  );
}

function ConvertToFullCTA({ onConvert, converting }) {
  return (
    <div className="card-flat p-6 border-2 border-dashed border-[#0B2447] mb-8">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-[#0B2447] rounded-sm flex items-center justify-center flex-shrink-0">
          <Microscope size={24} className="text-white" weight="duotone" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-[#0B2447] mb-1">Need Exposure-Based Assessment?</h3>
          <p className="text-sm text-[#4B5563] mb-3">
            Convert this hazard screening to a full QRA with SED and Margin of Safety calculations for each ingredient.
          </p>
          <Button onClick={onConvert} disabled={converting} className="btn-primary" data-testid="convert-to-full-cta-btn">
            {converting ? 'Converting...' : <><ArrowRight size={18} className="mr-2" />Convert to Full Assessment</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SummaryStats({ stats }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <div className="card-flat p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2">Total Ingredients</p>
        <p className="text-3xl font-black text-[#111827] tabular-nums">{stats.total}</p>
      </div>
      <div className="card-flat p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#03543F] mb-2">Acceptable</p>
        <p className="text-3xl font-black text-[#0E9F6E] tabular-nums">{stats.acceptable}</p>
      </div>
      <div className="card-flat p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#723B13] mb-2">Needs Review</p>
        <p className="text-3xl font-black text-[#E3A008] tabular-nums">{stats.needsReview}</p>
      </div>
      <div className="card-flat p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9B1C1C] mb-2">Not Acceptable</p>
        <p className="text-3xl font-black text-[#F05252] tabular-nums">{stats.notAcceptable}</p>
      </div>
    </div>
  );
}


export default function AssessmentPage() {
  const { id } = useParams();
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars
  const [formulation, setFormulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureStatus, setCaptureStatus] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [documents, setDocuments] = useState([]);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [newIngredients, setNewIngredients] = useState([]);
  const [versionNote, setVersionNote] = useState('');
  const [creatingVersion, setCreatingVersion] = useState(false);

  const fetchFormulation = useCallback(async () => {
    try {
      const [formRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/formulations/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/documents`, { params: { formulation_id: id }, withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setFormulation(formRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Error fetching formulation:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchFormulation(); }, [fetchFormulation]);

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/api/formulations/${id}/report`, { withCredentials: true, responseType: 'blob' });
      // Use filename from Content-Disposition header if available
      const disposition = response.headers['content-disposition'];
      let filename = mode === 'hazard'
        ? `AG_Screening_${formulation?.metadata?.product_name || 'Report'}.pdf`
        : `AG_CPSR_${formulation?.metadata?.product_name || 'Report'}.pdf`;
      if (disposition) {
        const match = disposition.match(/filename=(.+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleConvertToFull = async () => {
    setConverting(true);
    try {
      await axios.post(`${API_URL}/api/formulations/${id}/convert-to-full`, {}, { withCredentials: true });
      await fetchFormulation();
    } catch (error) {
      console.error('Error converting:', error);
    } finally {
      setConverting(false);
    }
  };

  const handleCaptureToLibrary = async () => {
    setCapturing(true); setCaptureStatus('');
    try {
      const resp = await axios.post(
        `${API_URL}/api/ingredients/private/capture-from-formulation/${id}`,
        { tag: `REF: AG-${id?.slice(0, 8).toUpperCase()}` },
        { withCredentials: true }
      );
      const { created, updated, status_applied } = resp.data;
      setCaptureStatus(`Saved to Library · ${created} new · ${updated} updated · marked "${status_applied}"`);
      setTimeout(() => setCaptureStatus(''), 4500);
    } catch (e) {
      console.error('Capture error:', e);
      setCaptureStatus('Failed to save to library');
      setTimeout(() => setCaptureStatus(''), 4500);
    } finally {
      setCapturing(false);
    }
  };

  const openNewVersion = () => {
    // Seed dialog with current ingredients so the user edits a copy
    setNewIngredients((formulation?.ingredients || []).map(i => ({
      inci_name: i.inci_name || '',
      cas_number: i.cas_number || '',
      concentration: i.concentration ?? '',
      function: i.function || '',
    })));
    setVersionNote('');
    setNewVersionOpen(true);
  };

  const handleSaveNewVersion = async () => {
    setCreatingVersion(true);
    try {
      const cleanIngs = newIngredients
        .filter(i => i.inci_name.trim())
        .map(i => ({
          inci_name: i.inci_name.trim(),
          cas_number: i.cas_number?.trim() || null,
          concentration: parseFloat(i.concentration) || 0,
          function: i.function?.trim() || null,
        }));
      const resp = await axios.post(
        `${API_URL}/api/formulations/${id}/new-version`,
        { ingredients: cleanIngs, note: versionNote || null },
        { withCredentials: true }
      );
      setNewVersionOpen(false);
      navigate(`/assessment/${resp.data.id}`);
    } catch (e) {
      console.error('New version error:', e);
    } finally {
      setCreatingVersion(false);
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}/download`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading doc:', error);
    }
  };

  const toggleRowExpand = (index) => {
    setExpandedRows(prev => {
      const n = new Set(prev);
      if (n.has(index)) n.delete(index); else n.add(index);
      return n;
    });
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#0B2447] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!formulation) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-[#111827] mb-2">Assessment not found</h2>
        <Link to="/dashboard" className="text-[#2563EB] hover:underline">Return to dashboard</Link>
      </div>
    </div>
  );

  const { metadata, assessment_results, overall_status, admin_notes, admin_override, assessment_mode, fragrance_results, claim_evaluations, exposure_assumptions, sign_off } = formulation;
  const mode = assessment_mode || 'full';
  const displayStatus = admin_override || overall_status;

  const stats = {
    total: assessment_results.length,
    acceptable: assessment_results.filter(r => r.risk_level === 'acceptable').length,
    needsReview: assessment_results.filter(r => r.risk_level === 'needs_review').length,
    notAcceptable: assessment_results.filter(r => r.risk_level === 'not_acceptable').length
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />

      {/* Action bar */}
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between border-b border-gray-100">
        <Link to="/dashboard" className="text-sm text-[#4B5563] hover:text-[#0B2447] flex items-center gap-1">
          <ArrowLeft size={16} /> Back to Portal
        </Link>
        <div className="flex items-center gap-3">
          {mode === 'hazard' && (
            <Button onClick={handleConvertToFull} disabled={converting} variant="outline" className="rounded-sm h-9 border-[#0B2447] text-[#0B2447] hover:bg-[#0B2447] hover:text-white text-sm" data-testid="convert-to-full-btn">
              {converting ? 'Converting...' : <><ArrowRight size={16} className="mr-1.5" />Convert to Full</>}
            </Button>
          )}
          <Button onClick={handleCaptureToLibrary} disabled={capturing} variant="outline" className="rounded-sm h-9 text-sm" data-testid="capture-to-library-btn">
            <BookOpen size={16} className="mr-1.5" />{capturing ? 'Saving...' : 'Save to Library'}
          </Button>
          <Button onClick={openNewVersion} variant="outline" className="rounded-sm h-9 text-sm" data-testid="new-version-btn">
            <GitBranch size={16} className="mr-1.5" /> New Version
          </Button>
          <Button onClick={handleDownloadReport} disabled={downloading} className="btn-primary h-9 text-sm" data-testid="download-report-btn">
            <DownloadSimple size={16} className="mr-1.5" />{downloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>
      </div>

      {captureStatus && (
        <div className="max-w-7xl mx-auto px-6 pt-3">
          <div className="p-2 bg-[#DEF7EC] border border-[#31C48D] text-[#03543F] text-xs font-semibold rounded-sm" data-testid="capture-status">
            {captureStatus}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Report Type Banner */}
        {mode === 'full' && !sign_off && (
          <div className="mb-4 p-3 bg-[#FDE8E8] border border-[#F8B4B4] rounded-sm flex items-center gap-2" data-testid="pending-signoff-banner">
            <Warning size={18} className="text-[#9B1C1C] flex-shrink-0" />
            <p className="text-sm font-semibold text-[#9B1C1C]">
              Pending Aᴳ Toxicologist Review — Report requires sign-off before regulatory use
            </p>
          </div>
        )}
        {mode === 'full' && sign_off && (
          <div className="mb-4 p-3 bg-[#DEF7EC] border border-[#31C48D] rounded-sm" data-testid="signed-off-banner">
            <p className="text-sm font-semibold text-[#03543F]">
              Signed by {sign_off.assessor_name}, {sign_off.assessor_title} — {sign_off.organization} — {sign_off.date}
            </p>
          </div>
        )}

        <div className="mb-2">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563]">
            {mode === 'hazard' ? 'Toxicological Screening Report — Pre-CPSR / Annex I Part A' : 'Cosmetic Product Safety Assessment — Annex I Part B'}
          </p>
          <p className="text-[10px] text-[#4B5563] font-mono">REF: AG-{formulation.id?.slice(0, 8).toUpperCase()}</p>
        </div>

        {/* Product Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <ModeBadge mode={mode} />
                {metadata.product_category && metadata.product_category !== 'custom' && (
                  <span className="text-xs font-mono px-2 py-0.5 bg-[#F3F4F6] border border-gray-200 rounded-sm text-[#111827]">{metadata.product_type || metadata.product_category}</span>
                )}
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-[#111827]">{metadata.product_name}</h1>
              <p className="text-[#4B5563] mt-1">
                {metadata.product_category && metadata.product_category !== 'custom' && <>{metadata.application_area} · </>}
                {metadata.target_population && <>{metadata.target_population} · </>}
                <span className="tabular-nums">{new Date(formulation.created_at).toLocaleDateString()}</span>
              </p>
            </div>
            <StatusBadge status={displayStatus} size="large" />
          </div>

          {admin_override && (
            <div className="mt-4 p-4 bg-[#E1EFFE] border border-[#A4CAFE] rounded-sm">
              <p className="text-sm font-semibold text-[#1E429F] flex items-center gap-2"><Info size={16} /> Expert Review Applied</p>
              {admin_notes && <p className="text-sm text-[#1E429F] mt-1">{admin_notes}</p>}
            </div>
          )}

          <ExposureAssumptions metadata={metadata} mode={mode} />
        </div>

        <SummaryStats stats={stats} />

        {/* Assessment Results Table */}
        <div className="card-flat overflow-hidden mb-8">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-[#111827]">{mode === 'hazard' ? 'Hazard Screening Results' : 'Ingredient Assessment'}</h2>
            <p className="text-sm text-[#4B5563]">{mode === 'hazard' ? 'Qualitative hazard data and regulatory flags.' : 'MoS = NOAEL / SED. MoS >= 100 is generally acceptable.'}</p>
          </div>
          <div className="overflow-x-auto">
            {mode === 'hazard' ? (
              <HazardTable results={assessment_results} expandedRows={expandedRows} onToggleRow={toggleRowExpand} />
            ) : (
              <FullQRATable results={assessment_results} expandedRows={expandedRows} onToggleRow={toggleRowExpand} />
            )}
          </div>
        </div>

        <SupportingDocuments documents={documents} onDownload={handleDownloadDoc} />

        {/* Fragrance Results */}
        <FragranceResultsSection fragranceResults={fragrance_results} mode={mode} />

        {/* Claim Support Review */}
        <ClaimResultsSection claimEvaluations={claim_evaluations} exposureAssumptions={exposure_assumptions} />

        {/* Pet Product Safety Screen — auto-triggered for Pet Product categories */}
        <PetAssessmentSection formulation={formulation} />

        {/* Version Timeline & Ingredient Diff (only renders if 2+ versions exist) */}
        <VersionDiffSection formulation={formulation} />

        {mode === 'hazard' && <ConvertToFullCTA onConvert={handleConvertToFull} converting={converting} />}

        {/* Structure Lookup · Read-Across · QSAR (server-side QSAR) */}
        <div className="mb-8">
          <StructureLookupWidget />
        </div>

        {/* Copilot Notes — pinned scientific findings from ToxIntel Copilot™ */}
        <CopilotNotesPanel formulationId={id} />

        {/* Disclaimer */}
        <div className="p-4 bg-[#F3F4F6] border border-gray-200 rounded-sm">
          <p className="text-xs text-[#4B5563]">
            <strong>Disclaimer:</strong> This report is for screening purposes only.
            {mode === 'hazard'
              ? ' Hazard screening provides qualitative identification of ingredient hazards and regulatory flags. It does not include exposure calculations or quantitative risk assessment.'
              : ' The results are based on automated calculations using SCCS/IFRA-aligned exposure defaults and should not be considered a complete safety assessment.'}
            {' '}Final product safety must be evaluated by a qualified toxicologist. The Agyekum Group accepts no liability for decisions made based on this screening report.
          </p>
        </div>
      </main>

      {/* New Version Dialog — edit ingredients and save with a note. */}
      <Dialog open={newVersionOpen} onOpenChange={setNewVersionOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col" data-testid="new-version-dialog">
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              Edit ingredients below. The diff vs the current version will be auto-computed and saved with the new version.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-2 -mr-2 pr-2">
            {newIngredients.map((ing, i) => (
              <div key={`nv-${i}`} className="flex gap-2 items-start">
                <Input value={ing.inci_name} onChange={e => setNewIngredients(rs => rs.map((r, idx) => idx === i ? { ...r, inci_name: e.target.value } : r))}
                  placeholder="INCI name" className="h-9 text-sm flex-1" data-testid={`nv-inci-${i}`} />
                <Input value={ing.cas_number} onChange={e => setNewIngredients(rs => rs.map((r, idx) => idx === i ? { ...r, cas_number: e.target.value } : r))}
                  placeholder="CAS" className="h-9 text-sm w-28" />
                <Input type="number" step="0.01" value={ing.concentration} onChange={e => setNewIngredients(rs => rs.map((r, idx) => idx === i ? { ...r, concentration: e.target.value } : r))}
                  placeholder="%" className="h-9 text-sm w-20" data-testid={`nv-conc-${i}`} />
                <Input value={ing.function} onChange={e => setNewIngredients(rs => rs.map((r, idx) => idx === i ? { ...r, function: e.target.value } : r))}
                  placeholder="Function" className="h-9 text-sm w-32" />
                <Button variant="outline" onClick={() => setNewIngredients(rs => rs.filter((_, idx) => idx !== i))}
                  className="h-9 w-9 p-0 rounded text-[#9B1C1C] hover:bg-[#FDE8E8] flex-shrink-0"
                  data-testid={`nv-remove-${i}`}>
                  <X size={14} />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={() => setNewIngredients(rs => [...rs, { inci_name: '', cas_number: '', concentration: '', function: '' }])}
              className="h-9 rounded text-xs" data-testid="nv-add-row">
              <Plus size={14} className="mr-1" /> Add ingredient
            </Button>
          </div>

          <div>
            <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">Version note (optional)</label>
            <Textarea value={versionNote} onChange={e => setVersionNote(e.target.value)} rows={2}
              placeholder="e.g., Removed Phenoxyethanol; bumped Niacinamide to 4%."
              className="text-sm mt-1" data-testid="nv-note-input" />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNewVersionOpen(false)} className="rounded">Cancel</Button>
            <Button onClick={handleSaveNewVersion} disabled={creatingVersion || newIngredients.length === 0}
              className="btn-primary" data-testid="nv-save-btn">
              {creatingVersion ? 'Creating…' : 'Create New Version'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
